// Size estimation — the post-allGreen "how big is this really" step. The
// repo-aware sandbox agent maps every VERIFIED acceptance criterion to the
// files it would touch and reports coarse buckets; the server recomputes the
// aggregate and decides `splitRecommended` itself (the model reports numbers,
// never the verdict).
//
// Privacy contract (same as `implementationPlan.planMarkdown`): the
// per-criterion `files` lists carry full repo paths and are PERSISTED ONLY —
// they never ride a tool result echo or a bus event. The client sees counts +
// buckets, nothing structural.
//
// Buckets, not line counts: a LoC integer would be false precision from a
// static read of the repo. The bands below are prompt guidance; the enum is
// the wire contract.

import { z } from "zod";

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** Coarse LoC bands. Per-file guidance: XS <25, S 25–75, M 75–250, L 250–600,
 *  XL >600. Overall guidance: XS <50, S <200, M <500, L <1500, XL ≥1500. */
export const LOC_BUCKETS = ["XS", "S", "M", "L", "XL"] as const;
export type LocBucket = (typeof LOC_BUCKETS)[number];
export const locBucketSchema = z.enum(LOC_BUCKETS);

/** True when `a` is the same band as `b` or a bigger one. */
export const locBucketAtLeast = (a: LocBucket, b: LocBucket): boolean =>
  LOC_BUCKETS.indexOf(a) >= LOC_BUCKETS.indexOf(b);

/**
 * "5 points" / "5" → 5; non-numeric or absent → undefined. THE single
 * story-point parser: the split policy (propose_size_estimate) and the GitLab
 * weight write (apply-update-issue) MUST read the same number, or the policy
 * audit log could disagree with what was written — so this is the one copy.
 * The low end of a range ("5-8" → 5) is intentional and shared by both callers.
 * The regex uses no lookbehind (ships to apps/web; Safari-14 safe).
 */
export const parseStoryPointsValue = (v: string | undefined): number | undefined => {
  if (v === undefined) return undefined;
  const m = /\d+/u.exec(v);
  if (!m) return undefined;
  const n = Number.parseInt(m[0], 10);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

/**
 * The modified-Fibonacci story-point scale. Lives here rather than in
 * `@loom/workflows` because both the estimator (which snaps to it) and the web
 * client (which offers it as choices) need it, and `apps/web` must not depend on
 * the server-side workflow package.
 *
 * NOT the same axis as {@link LOC_BUCKETS}: that is a lines-of-code band for the
 * file-level size estimate, this is relative effort. The repo deliberately never
 * maps one onto the other — doing so would invent a number the user never chose,
 * and `parseStoryPointsValue` feeds both the GitLab weight and the split-policy
 * audit, which must agree on a real value rather than on a guess.
 */
export const STORY_POINT_SCALE = [1, 2, 3, 5, 8, 13, 21] as const;

export const SIZING_CONFIDENCES = ["low", "medium", "high"] as const;
export type SizingConfidence = (typeof SIZING_CONFIDENCES)[number];
export const sizingConfidenceSchema = z.enum(SIZING_CONFIDENCES);

/** Closed cross-cutting-risk registry the estimation prompt offers. Closed on
 *  purpose: free-text risks would be unrenderable and unrankable. */
export const SIZING_RISKS = [
  "db_migration",
  "api_contract_change",
  "auth_or_permissions",
  "data_backfill",
  "cross_service",
  "ui_and_backend",
  "external_integration",
  "performance_sensitive",
  "i18n",
] as const;
export type SizingRisk = (typeof SIZING_RISKS)[number];
export const sizingRiskSchema = z.enum(SIZING_RISKS);

// ---------------------------------------------------------------------------
// propose_size_estimate INPUT (model-facing). `criterionId` values must match
// the server's own enumeration of the verified acceptance_criteria green
// ("AC1".."ACn" — see `criterionIdsFor`); the executor rejects unknown ids,
// duplicates, AND omissions, which is the first layer of the "no feature left
// out" guarantee. Plain `z.object` (no `.refine()` — omit/strip pattern).
// ---------------------------------------------------------------------------

export const sizingFileSchema = z.object({
  /** Repo path relative to the workspace root. PERSISTED ONLY — never echoed. */
  path: z.string().min(1).max(400),
  change: z.enum(["create", "modify"]),
  locBucket: locBucketSchema,
});
export type SizingFile = z.infer<typeof sizingFileSchema>;

export const sizingCriterionSchema = z.object({
  /** "AC1".."ACn" — the server-enumerated id of a verified criterion. */
  criterionId: z.string().min(1).max(10),
  /** Verbatim criterion text; the executor asserts substring containment in
   *  the persisted acceptance_criteria green (anti-restatement belt). */
  criterionQuote: z.string().min(1).max(2000),
  files: z.array(sizingFileSchema).max(40),
  locBucket: locBucketSchema,
  confidence: sizingConfidenceSchema,
  notes: z.string().max(300).optional(),
});
export type SizingCriterion = z.infer<typeof sizingCriterionSchema>;

export const proposeSizeEstimateInputShape = {
  issueIid: z.number().int().positive(),
  criteria: z.array(sizingCriterionSchema).min(1).max(30),
  overall: z.object({
    filesTouched: z.number().int().nonnegative(),
    locBucket: locBucketSchema,
    confidence: sizingConfidenceSchema,
  }),
  risks: z.array(sizingRiskSchema).default([]),
  /** Plain business language, no paths/technical terms — the ONLY sizing prose
   *  that rides to the client (like `businessSummary`). */
  sizeSummary: z.string().min(1).max(1200),
} as const;
export const proposeSizeEstimateInputSchema = z.object(
  proposeSizeEstimateInputShape,
);
export type ProposeSizeEstimateInput = z.infer<
  typeof proposeSizeEstimateInputSchema
>;

// ---------------------------------------------------------------------------
// Split policy — `.loom.yml breakdown:` block. Defined here (not in
// `config/loom-config.ts`) for the same one-directional-import reason as
// `readinessSpecSchema` living in `spec.ts`: config imports readiness, never
// the reverse. Keys are snake_case to match the rest of `.loom.yml`.
// ---------------------------------------------------------------------------

export const breakdownConfigSchema = z
  .object({
    enabled: z.boolean().optional(),
    /** Recommend a split when distinct files touched exceeds this. */
    max_files: z.number().int().positive().optional(),
    /** …or when the overall LoC bucket is at least this band. */
    min_loc_bucket: locBucketSchema.optional(),
    /** …or when the verified story_points reach this value. */
    story_points_threshold: z.number().int().positive().optional(),
    max_children: z.number().int().min(2).max(12).optional(),
  })
  .strict();
export type BreakdownConfig = z.infer<typeof breakdownConfigSchema>;

export interface BreakdownPolicy {
  readonly enabled: boolean;
  readonly maxFiles: number;
  readonly minLocBucket: LocBucket;
  readonly storyPointsThreshold: number;
  readonly maxChildren: number;
}

export const DEFAULT_BREAKDOWN_POLICY: BreakdownPolicy = {
  enabled: true,
  maxFiles: 8,
  minLocBucket: "L",
  storyPointsThreshold: 8,
  maxChildren: 6,
};

/**
 * Resolve the split policy for a project from its parsed `.loom.yml` (passed
 * as `unknown`, like `resolveReadinessSpec`): `safeParse` the optional
 * `breakdown` block and DEFAULT on any failure, so a malformed section can
 * never crash the sizing gate.
 *
 * Live-path note: in the deployed wiring the config reaching here has ALREADY
 * been validated by the strict `LoomConfigSchema` at session load, and a
 * malformed `breakdown:` block there defaults the ENTIRE `.loom.yml` (not just
 * this block) — same as `resolveReadinessSpec`. The `safeParse` below only
 * isolates the block when fed a raw/partial config directly (unit tests).
 */
export const resolveBreakdownPolicy = (loomConfig?: unknown): BreakdownPolicy => {
  const candidate = (loomConfig as { breakdown?: unknown } | null | undefined)
    ?.breakdown;
  if (candidate === undefined) return DEFAULT_BREAKDOWN_POLICY;
  const parsed = breakdownConfigSchema.safeParse(candidate);
  if (!parsed.success) return DEFAULT_BREAKDOWN_POLICY;
  const c = parsed.data;
  return {
    enabled: c.enabled ?? DEFAULT_BREAKDOWN_POLICY.enabled,
    maxFiles: c.max_files ?? DEFAULT_BREAKDOWN_POLICY.maxFiles,
    minLocBucket: c.min_loc_bucket ?? DEFAULT_BREAKDOWN_POLICY.minLocBucket,
    storyPointsThreshold:
      c.story_points_threshold ?? DEFAULT_BREAKDOWN_POLICY.storyPointsThreshold,
    maxChildren: c.max_children ?? DEFAULT_BREAKDOWN_POLICY.maxChildren,
  };
};

export interface SizePolicyInput {
  readonly criteriaCount: number;
  readonly filesTouchedComputed: number;
  readonly overallLocBucket: LocBucket;
  /** Parsed from the verified story_points green when present. */
  readonly storyPoints?: number;
}

/**
 * The three size outcomes, kept distinct so a big ticket is never mislabeled
 * "small":
 *   - `single`           — under every threshold; build it as one ticket.
 *   - `split`            — over a threshold AND ≥2 criteria; a split is both
 *                          warranted and mathematically possible.
 *   - `oversized_single` — over a threshold but <2 criteria; too big to build
 *                          comfortably, yet it can't be partitioned by
 *                          acceptance criterion until it's refined into more.
 */
export type SizeVerdict = "single" | "split" | "oversized_single";

/** Whether the size gate trips, independent of how many criteria there are —
 *  evaluated over SERVER-recomputed numbers (distinct paths across criteria),
 *  never the model's `overall.filesTouched`. */
const sizeExceedsThreshold = (
  policy: BreakdownPolicy,
  input: SizePolicyInput,
): boolean =>
  locBucketAtLeast(input.overallLocBucket, policy.minLocBucket) ||
  input.filesTouchedComputed > policy.maxFiles ||
  (input.storyPoints !== undefined &&
    input.storyPoints >= policy.storyPointsThreshold);

/**
 * The server-side size verdict. The `criteriaCount < 2` case does NOT collapse
 * to "single" — a single-criterion ticket can still be too big, it just can't
 * be split by criterion, so it's reported as `oversized_single` (the UI asks
 * the user to refine it into separate criteria first).
 */
export const evaluateSizeVerdict = (
  policy: BreakdownPolicy,
  input: SizePolicyInput,
): SizeVerdict => {
  if (!policy.enabled || !sizeExceedsThreshold(policy, input)) return "single";
  return input.criteriaCount >= 2 ? "split" : "oversized_single";
};

/**
 * `true` only when a split is both warranted and possible. Thin wrapper over
 * `evaluateSizeVerdict` — the persisted `splitRecommended` bit and every caller
 * that only cares "do we offer the split flow" stay on this.
 */
export const evaluateSplitPolicy = (
  policy: BreakdownPolicy,
  input: SizePolicyInput,
): boolean => evaluateSizeVerdict(policy, input) === "split";

// ---------------------------------------------------------------------------
// Persisted estimate — `CardState.sizeEstimate`. Input + server stamps.
// Like `implementationPlan`, it lives OUTSIDE `fields`, so persisting it never
// touches the acceptToken's slot hash.
// ---------------------------------------------------------------------------

export const cardSizeEstimateSchema = z.object({
  criteria: z.array(sizingCriterionSchema),
  overall: z.object({
    filesTouched: z.number().int().nonnegative(),
    locBucket: locBucketSchema,
    confidence: sizingConfidenceSchema,
  }),
  risks: z.array(sizingRiskSchema),
  sizeSummary: z.string(),
  // ---- server-stamped (never model-writable) ----
  /** Distinct `files[].path` count across criteria — the number the policy ran on. */
  filesTouchedComputed: z.number().int().nonnegative(),
  splitRecommended: z.boolean(),
  /** Over a size threshold but with <2 criteria — too big to build as one, yet
   *  not AC-partitionable until refined. OPTIONAL: rows persisted before this
   *  field stay parseable (deploy-skew safe). */
  oversizedSingleCriterion: z.boolean().optional(),
  /**
   * The criteria moved after this estimate was evaluated, so `splitRecommended`
   * is a verdict about a list that no longer exists.
   *
   * The wire could not previously say "unknown": the rebase hard-set
   * `splitRecommended: false`, which reads on the client as a real "one ticket
   * is right" and silently skipped the split step for exactly the issues the
   * feature targets. This flag lets the UI say we do not know and ask, which
   * costs nothing, instead of inventing a verdict or forcing a billed re-size.
   * OPTIONAL — a client that predates it reads `splitRecommended` as before.
   */
  verdictStale: z.boolean().optional(),
  /** The policy values in force at evaluation time (audit/debug). `enabled` is
   *  OPTIONAL so CardState rows persisted before it was recorded stay parseable. */
  policySnapshot: z.object({
    enabled: z.boolean().optional(),
    maxFiles: z.number().int(),
    minLocBucket: locBucketSchema,
    storyPointsThreshold: z.number().int(),
    maxChildren: z.number().int(),
  }),
  createdAtMs: z.number().int().nonnegative(),
  updatedAtMs: z.number().int().nonnegative(),
});
export type CardSizeEstimate = z.infer<typeof cardSizeEstimateSchema>;
